import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { pickStaffPageEditorial, usePublicStaffBanners } from '../../hooks/useSitePageUpdates';
import {
  SITE_PAGE_FEATURE_DEFS,
  hasEditorialContent,
  renderSimpleMd,
} from '../../lib/sitePageConfig';

/**
 * Staff-configured editorial strip + enabled “page features” chips (prefix-matched route).
 */
export const SiteStaffPageModule: React.FC = () => {
  const { pathname } = useLocation();
  const byPath = usePublicStaffBanners();

  const row = useMemo(() => pickStaffPageEditorial(pathname, byPath), [pathname, byPath]);
  const cfg = row?.pageConfig;
  if (!cfg?.showContentStrip) return null;

  const enabledFeatures = SITE_PAGE_FEATURE_DEFS.filter((d) => cfg.features[d.key]);
  const hasEdit = hasEditorialContent(cfg);
  if (!hasEdit && enabledFeatures.length === 0) return null;

  return (
    <div className="border-b border-white/[0.08] bg-gradient-to-b from-[#0d0d0d] via-[#080808] to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-5 sm:py-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-500/85 font-bold mb-3">
          <Sparkles size={14} className="text-emerald-400/90" aria-hidden />
          From the team
        </div>

        {cfg.pageTagline.trim() ? (
          <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight mb-3">{cfg.pageTagline}</h2>
        ) : null}

        {cfg.spotlight.title.trim() || cfg.spotlight.body.trim() ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 mb-4 flex flex-col sm:flex-row gap-4">
            {cfg.spotlight.imageUrl.trim() ? (
              <img
                src={cfg.spotlight.imageUrl}
                alt=""
                className="w-full sm:w-40 h-40 sm:h-28 object-cover rounded-xl bg-gray-900 shrink-0"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              {cfg.spotlight.title.trim() ? (
                <h3 className="text-base font-bold text-white">{cfg.spotlight.title}</h3>
              ) : null}
              {cfg.spotlight.subtitle.trim() ? (
                <p className="text-sm text-amber-200/85 mt-1">{cfg.spotlight.subtitle}</p>
              ) : null}
              {cfg.spotlight.body.trim() ? (
                <p className="text-sm text-gray-400 mt-2 leading-relaxed">{cfg.spotlight.body}</p>
              ) : null}
              {cfg.spotlight.ctaLabel.trim() && cfg.spotlight.ctaHref.trim() ? (
                <div className="mt-3">
                  {/^https?:\/\//i.test(cfg.spotlight.ctaHref) ? (
                    <a
                      href={cfg.spotlight.ctaHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex text-sm font-semibold text-amber-400 hover:text-amber-300"
                    >
                      {cfg.spotlight.ctaLabel} →
                    </a>
                  ) : (
                    <Link
                      to={
                        cfg.spotlight.ctaHref.startsWith('/')
                          ? cfg.spotlight.ctaHref
                          : `/${cfg.spotlight.ctaHref}`
                      }
                      className="inline-flex text-sm font-semibold text-amber-400 hover:text-amber-300"
                    >
                      {cfg.spotlight.ctaLabel} →
                    </Link>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {cfg.blogs.some((b) => b.title.trim() || b.excerpt.trim()) ? (
          <div className="mb-4">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Stories & updates</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cfg.blogs.filter((b) => b.title.trim() || b.excerpt.trim()).map((b) => {
                const inner = (
                  <>
                    <p className="text-sm font-semibold text-white line-clamp-2">{b.title || 'Untitled'}</p>
                    {b.excerpt.trim() ? (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-3 leading-relaxed">{b.excerpt}</p>
                    ) : null}
                  </>
                );
                const cardClass =
                  'block rounded-xl border border-gray-800 bg-gray-950/60 p-4 hover:border-amber-800/40 transition-colors';
                if (b.href.trim()) {
                  if (/^https?:\/\//i.test(b.href)) {
                    return (
                      <a key={b.id} href={b.href} target="_blank" rel="noopener noreferrer" className={cardClass}>
                        {inner}
                      </a>
                    );
                  }
                  return (
                    <Link key={b.id} to={b.href.startsWith('/') ? b.href : `/${b.href}`} className={cardClass}>
                      {inner}
                    </Link>
                  );
                }
                return (
                  <div key={b.id} className={cardClass}>
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {cfg.pageBodyMd.trim() ? (
          <div className="prose prose-invert prose-sm max-w-none border-t border-white/5 pt-4">
            {renderSimpleMd(cfg.pageBodyMd)}
          </div>
        ) : null}

        {enabledFeatures.length > 0 ? (
          <div className="mt-4 pt-4 border-t border-white/5">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Page modules enabled</p>
            <div className="flex flex-wrap gap-1.5">
              {enabledFeatures.map((d) => (
                <span
                  key={d.key}
                  className="text-xs px-2 py-1 rounded-lg bg-emerald-950/50 text-emerald-100/90 border border-emerald-800/35"
                >
                  {d.label}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
